import { FormProvider } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { Box, Stack } from "@seneca/senekit";

import { MultiLineTextPlaceholder } from "features/common/components/TextPlaceholder";
import { SignUpCardContent } from "features/sign-up/common-routes/components/Layout";
import ErrorMessage from "features/sign-up/components/ErrorMessage";

import {
  ForgotPasswordLink,
  SignInEmailInput,
  SignInPasswordInput,
  SignInSubmitButton
} from "./components";
import useSignInWithEmailAndOptionalPasswordForm from "./hooks/useSignInWithEmailAndOptionalPasswordForm";

type Props = {
  loading?: boolean;
  forgotPasswordLink: string;
  prefilledEmail?: string;
  redirectAuthenticatedUserTo?: string;
  onSignInWithPasswordSuccess?: () => void;
  children: {
    heading: React.ReactNode;
    alternativeLoginLink: React.ReactNode;
  };
};

export default function SignInWithOptionalPasswordForm({
  loading,
  forgotPasswordLink,
  prefilledEmail,
  onSignInWithPasswordSuccess,
  redirectAuthenticatedUserTo,
  children: { heading, alternativeLoginLink }
}: Props) {
  const location = useLocation();

  const { form, errorMessage, submitButtonText, onSubmit } =
    useSignInWithEmailAndOptionalPasswordForm({
      prefilledEmail,
      onSignInWithPasswordSuccess,
      redirectAuthenticatedUserTo
    });

  const forgotPasswordLocation = {
    ...location,
    pathname: forgotPasswordLink
  };

  return (
    <FormProvider {...form}>
      <SignUpCardContent>
        {{
          body: (
            <Stack space="xlarge">
              <Stack space={["xlarge", "xxlarge"]}>
                {heading}

                <MultiLineTextPlaceholder
                  loading={loading}
                  placeholderHeight="50px"
                  placeholderWidth="100%"
                  numberOfLines={2}
                >
                  <Stack space="xlarge" align="center">
                    <SignInEmailInput
                      email={prefilledEmail}
                      autoFocus={!prefilledEmail}
                      onEnterKeyPress={onSubmit}
                    />
                    <SignInPasswordInput
                      autoFocus={!!prefilledEmail}
                      onEnterKeyPress={onSubmit}
                      placeholderText="Optional"
                      isOptional
                    />
                  </Stack>
                </MultiLineTextPlaceholder>
              </Stack>

              <Box display="flex" justifyContent="flex-start">
                <ForgotPasswordLink
                  forgotPasswordLink={forgotPasswordLocation}
                />
              </Box>
            </Stack>
          ),
          footer: (
            <Stack space="medium">
              {errorMessage && (
                <ErrorMessage>{errorMessage?.message}</ErrorMessage>
              )}

              <Stack space="xxlarge">
                <SignInSubmitButton
                  onSubmit={onSubmit}
                  loading={form.formState.isSubmitting}
                >
                  {submitButtonText}
                </SignInSubmitButton>

                {alternativeLoginLink}
              </Stack>
            </Stack>
          )
        }}
      </SignUpCardContent>
    </FormProvider>
  );
}
